import React, { useState } from 'react';
import { Bell, Globe, Moon, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/shared/Toast';
import api from '../../services/authService';
import CustomSelect from '../../components/shared/CustomSelect';

const VendorPreferences = () => {
  const { user, updateUser } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    email_notifications: user?.email_notifications ?? true,
    sms_notifications: user?.sms_notifications ?? false,
    booking_notifications: user?.booking_notifications ?? true,
    marketing_emails: user?.marketing_emails ?? false,
    language: user?.language ?? 'en',
    timezone: user?.timezone ?? 'UTC',
    theme: user?.theme ?? 'light'
  });

  const handlePreferenceChange = async (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    
    try {
      setLoading(true);
      const response = await api.put('/user/preferences', {
        [key]: value
      });
      
      if (response.data.success) {
        toast.addToast('Preferences updated successfully', 'success');
        updateUser({ ...user, ...preferences, [key]: value });
      }
    } catch (error) {
      toast.addToast('Failed to update preferences', 'error');
      // Revert on error
      setPreferences(prev => ({ ...prev, [key]: !value }));
    } finally {
      setLoading(false);
    }
  };

  const notificationSettings = [
    {
      key: 'email_notifications',
      label: 'Email Notifications',
      description: 'Receive notifications via email',
      icon: Mail
    },
    {
      key: 'sms_notifications',
      label: 'SMS Notifications',
      description: 'Receive notifications via SMS',
      icon: Bell
    },
    {
      key: 'booking_notifications',
      label: 'Booking Notifications',
      description: 'Get notified about new bookings and updates',
      icon: Bell
    },
    {
      key: 'marketing_emails',
      label: 'Marketing Emails',
      description: 'Receive promotional emails and updates',
      icon: Mail
    }
  ];

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Preferences</h1>
          <p className="text-sm text-gray-600">Customize your application preferences</p>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-gray-600" />
          Notification Preferences
        </h2>
        <div className="space-y-4">
          {notificationSettings.map((setting) => {
            const Icon = setting.icon;
            return (
              <div key={setting.key} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{setting.label}</p>
                    <p className="text-sm text-gray-500">{setting.description}</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences[setting.key]}
                    onChange={(e) => handlePreferenceChange(setting.key, e.target.checked)}
                    disabled={loading}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* Language & Region */}
      {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-gray-600" />
          Language & Region
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <CustomSelect
              label="Language"
              value={preferences.language}
              onChange={(value) => handlePreferenceChange('language', value)}
              disabled={loading}
              options={[
                { value: 'en', label: 'English' },
                { value: 'es', label: 'Spanish' },
                { value: 'fr', label: 'French' },
                { value: 'de', label: 'German' }
              ]}
              size="md"
            />
          </div>
          <div>
            <CustomSelect
              label="Timezone"
              value={preferences.timezone}
              onChange={(value) => handlePreferenceChange('timezone', value)}
              disabled={loading}
              options={[
                { value: 'UTC', label: 'UTC' },
                { value: 'America/New_York', label: 'Eastern Time (ET)' },
                { value: 'America/Chicago', label: 'Central Time (CT)' },
                { value: 'America/Denver', label: 'Mountain Time (MT)' },
                { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
                { value: 'Europe/London', label: 'London (GMT)' }
              ]}
              size="md"
            />
          </div>
        </div>
      </div> */}

      {/* Appearance */}
      {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Moon className="w-5 h-5 text-gray-600" />
          Appearance
        </h2>
        <div>
          <CustomSelect
            label="Theme"
            value={preferences.theme}
            onChange={(value) => handlePreferenceChange('theme', value)}
            disabled={loading}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
              { value: 'system', label: 'System' }
            ]}
            size="md"
            className="w-full md:w-64"
          />
        </div>
      </div> */}
    </>
  );
};

export default VendorPreferences;

